import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
    LoadingContext,
    M_StateActions,
    MTable,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Route CRUD page in a tab.
 */
const ROUTE_CRUD_ROUTE = "/home/routes/routeConfigCrud";

//endregion

//region Component

/**
 * Renders the Routes configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in tabs mode;
 * - keep the page visually aligned with other Config tabs through config-page.css;
 * - render the Routes MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - persist the current menu and selected route before opening View/Edit tabs.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @param {object|null|undefined} props.currentMenu - Current menu context.
 * @returns {JSX.Element} Routes configuration tab.
 */
export default function RouteConfig({
    roles,
    onCreateTab,
    toastMessages,
    currentMenu,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this tab.
     *
     * Important:
     * - Print is not part of the desired Routes workflow;
     * - Copy is disabled to keep route creation explicit.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy"],
        []
    );

    /**
     * Opens the Route CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the current menu in M_State;
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected route row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected route row.
     * @returns {void}
     */
    const openRouteCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "menu",
                    value: currentMenu,
                })
            );

            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "RouteLine",
                        value: row,
                    })
                );
            }

            const safeDescription = row?.Description ?? "";

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewRoute")
                    : operation === "View"
                        ? `${t("LabelViewRoute")} "${safeDescription}"`
                        : `${t("LabelEditRoute")} "${safeDescription}"`;

            onCreateTab(ROUTE_CRUD_ROUTE, tabLabel);
        },
        [currentMenu, dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Route workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openRouteCrud("View", row)],
                ["Create", () => openRouteCrud("Create")],
                ["Edit", (row) => openRouteCrud("Edit", row)],
            ]),
        [openRouteCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header config-page__header--tabs">
                <div className="config-page__title config-page__title--tabs">
                    {t("Routes")}
                </div>

                <div className="config-page__description config-page__description--tabs">
                    {t("InfoRoutes")}
                </div>
            </div>

            <div className="config-page__table config-page__table--tabs">
                <MTable
                    filtersOnTable
                    entityName="Routes"
                    roleName="Routes"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                    tabsRailWidth={250}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

RouteConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
    currentMenu: PropTypes.any,
};

//endregion